import { useState } from "react";
import { Mail, MapPin, Phone, Send } from "lucide-react";
import { api } from "@/lib/api";
import { CONTACT } from "@/constants/testIds";
import { toast } from "sonner";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "", inquiry_type: "general" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", form);
      toast.success("Message received. Our team will reply within a few hours.");
      setForm({ name: "", email: "", phone: "", message: "", inquiry_type: "general" });
    } catch {
      toast.error("Could not send. Please try again or email us directly.");
    } finally { setSending(false); }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="pt-32 pb-24 bg-ivory">
      <div className="container-tarzan grid grid-cols-1 lg:grid-cols-2 gap-16">
        <div>
          <div className="eyebrow mb-3">Concierge</div>
          <h1 className="font-serif text-5xl md:text-6xl text-ink tracking-tight leading-[1.02]">Talk to us.<br/>We're <em className="not-italic text-forest">actual humans.</em></h1>
          <p className="text-ink-soft mt-5 text-base md:text-lg leading-relaxed max-w-md">Need help picking the right home? Planning a milestone? Hosting a small wedding? Write to us — we typically reply within a few hours.</p>

          <div className="mt-12 space-y-6">
            <ContactRow icon={Phone} h="Call us" v={<a href="tel:+919491317777" className="text-ink hover:text-forest">+91 94913 17777</a>} />
            <ContactRow icon={Mail}  h="Email"   v={<a href="mailto:hello@tarzanretreat.com" className="text-ink hover:text-forest">hello@tarzanretreat.com</a>} />
            <ContactRow icon={MapPin} h="Tarzan Hyderabad Retreat" v="Nature and Home, opposite Anganwadi center, Amdapur, Telangana 501504" />
          </div>
        </div>

        <form onSubmit={submit} className="bg-white border border-forest/10 rounded-3xl p-8 md:p-10 luxe-shadow self-start">
          <h2 className="font-serif text-2xl text-ink mb-6">Send us a message</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Field label="Name" required>
              <input data-testid={CONTACT.name} value={form.name} onChange={set("name")} required className="w-full border-b border-forest/20 bg-transparent py-3 outline-none focus:border-forest" />
            </Field>
            <Field label="Email" required>
              <input data-testid={CONTACT.email} type="email" value={form.email} onChange={set("email")} required className="w-full border-b border-forest/20 bg-transparent py-3 outline-none focus:border-forest" />
            </Field>
            <Field label="Phone (optional)">
              <input data-testid={CONTACT.phone} value={form.phone} onChange={set("phone")} className="w-full border-b border-forest/20 bg-transparent py-3 outline-none focus:border-forest" />
            </Field>
            <Field label="Inquiry type">
              <select value={form.inquiry_type} onChange={set("inquiry_type")} className="w-full border-b border-forest/20 bg-transparent py-3 outline-none focus:border-forest">
                <option value="general">General</option>
                <option value="booking">Booking help</option>
                <option value="host">Become a host</option>
              </select>
            </Field>
            <Field label="Message" className="md:col-span-2" required>
              <textarea data-testid={CONTACT.message} value={form.message} onChange={set("message")} required rows={5} className="w-full border-b border-forest/20 bg-transparent py-3 outline-none focus:border-forest resize-none" />
            </Field>
          </div>
          <button data-testid={CONTACT.submit} disabled={sending} type="submit" className="mt-8 inline-flex items-center gap-2 bg-forest text-ivory px-7 py-3.5 rounded-full text-sm font-medium hover:bg-forest-700 transition-all disabled:opacity-50">
            {sending ? "Sending…" : "Send Message"} <Send className="h-4 w-4" />
          </button>
        </form>
      </div>
    </div>
  );
}

function ContactRow({ icon: Icon, h, v }) {
  return (
    <div className="flex items-start gap-4">
      <div className="h-11 w-11 rounded-full bg-forest-50 grid place-items-center shrink-0"><Icon className="h-4 w-4 text-forest" /></div>
      <div>
        <div className="text-xs uppercase tracking-[0.18em] text-ink-soft font-medium">{h}</div>
        <div className="text-ink mt-0.5">{v}</div>
      </div>
    </div>
  );
}

function Field({ label, children, required, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <div className="text-[10px] uppercase tracking-[0.18em] text-ink-soft font-medium">{label}{required && " *"}</div>
      {children}
    </label>
  );
}
